import React from 'react';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import styled from 'styled-components';

interface ImageCarouselProps {
  images: {
    src: string;
    alt: string;
  }[];
}

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    height: 70vh;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    height: 50vh;
  }
  
  .carousel .slide {
    background: transparent;
  }
  
  .carousel .control-dots {
    bottom: 2rem;
    
    .dot {
      background: ${({ theme }) => theme.colors.background};
      box-shadow: none;
      width: 10px;
      height: 10px;
      
      &.selected {
        background: ${({ theme }) => theme.colors.primary};
      }
    }
  }
  
  .carousel .control-arrow {
    opacity: 0;
    transition: opacity 0.3s ease;
    
    &:before {
      border-width: 0 3px 3px 0;
      padding: 10px;
    }
    
    &.control-prev:before {
      margin-right: -15px;
    }
    
    &.control-next:before {
      margin-left: -15px;
    }
  }
  
  &:hover .control-arrow {
    opacity: 1;
  }
`;

const CarouselImage = styled.div<{ backgroundImage: string }>`
  width: 100%;
  height: 100vh;
  background-image: url(${({ backgroundImage }) => backgroundImage});
  background-size: cover;
  background-position: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    height: 70vh;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    height: 50vh;
  }
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.5));
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 0 2rem;
`;

const Title = styled.h1`
  color: ${({ theme }) => theme.colors.background};
  font-size: 3.5rem;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 2.5rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.background};
  font-size: 1.2rem;
  max-width: 600px;
  margin-bottom: 2rem;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1rem;
  }
`;

const Button = styled.a`
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.background};
  padding: 0.8rem 2rem;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.accent};
  }
`;

const ImageCarousel: React.FC<ImageCarouselProps> = ({ images }) => {
  return (
    <CarouselContainer>
      <Carousel
        autoPlay
        infiniteLoop
        showStatus={false}
        showThumbs={false}
        interval={5000}
        transitionTime={500}
      >
        {images.map((image, index) => (
          <div key={index}>
            <CarouselImage backgroundImage={image.src} />
            <Overlay>
              <Title>Timeless Elegance</Title>
              <Subtitle>By Adriana Melone</Subtitle>
              <Button href="/collections">Explore Seasons</Button>
            </Overlay>
          </div>
        ))}
      </Carousel>
    </CarouselContainer>
  );
};

export default ImageCarousel;
